import { useState, useEffect, useRef, useCallback } from 'react';
import { ENABLE_NODE_GLOBE } from '../state/atoms';

const BITNODES_URL = 'https://bitnodes.io/api/v1/snapshots/latest/';
const SIDEWINDER_URL = 'https://2140data.net/api/s01/latest';
const SIDEWINDER_KEY = 'EUL9E54951LU3L5FA65M2D9KZQUH7LL05';
const REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour

const parseBitnodesResponse = (data) => {
  const nodes = [];
  for (const info of Object.values(data.nodes || {})) {
    const lat = info[8], lon = info[9];
    if (lat != null && lon != null && (lat !== 0 || lon !== 0)) {
      nodes.push({ lat, lon });
    }
  }
  return { nodes, total: data.total_nodes || nodes.length };
};

const parseSidewinderResponse = (data) => {
  const snapshot = data?.s01?.latest_snapshot;
  if (!snapshot) return null;
  const locations = snapshot.node_locations;
  if (locations?.length > 0) {
    return {
      nodes: locations.map(([lat, lon]) => ({ lat, lon })),
      total: snapshot.total_nodes || locations.length,
    };
  }
  if (snapshot.total_nodes) {
    return { nodes: null, total: snapshot.total_nodes };
  }
  return null;
};

export const useNodeData = () => {
  const [nodes, setNodes] = useState([]);
  const [totalNodes, setTotalNodes] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const fetchNodes = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    // Try bitnodes first (has full lat/lon data)
    try {
      const res = await fetch(BITNODES_URL);
      if (!res.ok) throw new Error('Bitnodes ' + res.status);
      const data = await res.json();
      if (data.detail) throw new Error(data.detail);
      const result = parseBitnodesResponse(data);
      if (result.nodes.length > 0) {
        setNodes(result.nodes);
        setTotalNodes(result.total);
        loadingRef.current = false;
        setLoading(false);
        return;
      }
    } catch (e) {
      console.warn('Bitnodes unavailable, trying sidewinder:', e.message);
    }

    // Fallback to sidewinder (count only, no fake locations)
    try {
      const res = await fetch(`${SIDEWINDER_URL}?api_key=${SIDEWINDER_KEY}`);
      if (!res.ok) throw new Error('Sidewinder ' + res.status);
      const data = await res.json();
      const result = parseSidewinderResponse(data);
      if (result) {
        if (result.nodes) setNodes(result.nodes);
        setTotalNodes(result.total);
        loadingRef.current = false;
        setLoading(false);
        return;
      }
    } catch (e) {
      console.warn('Sidewinder unavailable:', e.message);
    }

    loadingRef.current = false;
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!ENABLE_NODE_GLOBE) return;
    fetchNodes();
    const timer = setInterval(fetchNodes, REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchNodes]);

  return { nodes, totalNodes, loading };
};
